import { CheckCircle2, Mail, ShieldAlert, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAppBootstrap } from "@/context/AppBootstrapContext";
import { cn } from "@/lib/utils";
import {
  useGraphMailboxBanner,
  type GraphBannerVariant,
} from "@/hooks/useGraphMailboxBanner";

interface VariantStyle {
  container: string;
  icon: typeof Mail;
  iconClass: string;
}

const VARIANT_STYLES: Record<GraphBannerVariant, VariantStyle> = {
  connect: {
    container: "border-sky-500/40 bg-sky-500/10",
    icon: Mail,
    iconClass: "text-sky-600 dark:text-sky-400",
  },
  reauthorize: {
    container: "border-amber-500/40 bg-amber-500/10",
    icon: ShieldAlert,
    iconClass: "text-amber-600 dark:text-amber-400",
  },
  wrong_account: {
    container: "border-amber-500/40 bg-amber-500/10",
    icon: ShieldAlert,
    iconClass: "text-amber-600 dark:text-amber-400",
  },
  connected: {
    container: "border-emerald-500/40 bg-emerald-500/10",
    icon: CheckCircle2,
    iconClass: "text-emerald-600 dark:text-emerald-400",
  },
};

/**
 * App-wide onboarding banner shown to a user whose email matches a Microsoft
 * Graph mailbox. Prompts them to connect / re-authorize Outlook and confirms
 * success after the OAuth round-trip. Mounted once in the AppShell.
 */
export function GraphMailboxBanner() {
  const { phase } = useAppBootstrap();
  const banner = useGraphMailboxBanner();

  // Only surface once the splash screen has fully handed off to the shell, so
  // the banner animates in as part of the home load sequence rather than
  // flashing over the splash.
  if (phase !== "ready") return null;
  if (!banner) return null;

  const {
    variant,
    mailbox,
    detail,
    authorizing,
    dismissible,
    onAuthorize,
    onDismiss,
  } = banner;
  const style = VARIANT_STYLES[variant];
  const Icon = style.icon;
  const email = mailbox?.email;

  let title: string;
  let body: string;
  let actionLabel: string | null;

  switch (variant) {
    case "connect":
      title = "Connect your Outlook mailbox";
      body = email
        ? `Connect ${email} so Twido can read your freight emails on your behalf. Access is read-only.`
        : "Connect your Outlook mailbox so Twido can read your freight emails on your behalf. Access is read-only.";
      actionLabel = "Connect Outlook";
      break;
    case "reauthorize":
      title = "Your Outlook connection needs attention";
      body =
        "Reconnect your mailbox so Twido can keep syncing your freight emails.";
      actionLabel = "Re-authorize";
      break;
    case "wrong_account":
      title = "Wrong Microsoft account";
      body = email
        ? `You signed in with a different Microsoft account. Please sign in as ${email}.`
        : "You signed in with a different Microsoft account. Please sign in with your mailbox address.";
      actionLabel = "Try again";
      break;
    case "connected":
      title = "Outlook connected";
      body =
        "Twido will start syncing your mailbox. Your admin may run the first sync.";
      actionLabel = null;
      break;
  }

  return (
    <div className="px-1 pb-3 pt-1">
      <div
        className={cn(
          "flex items-start gap-3 rounded-lg border px-4 py-3",
          style.container,
        )}
      >
        <Icon className={cn("mt-0.5 size-5 shrink-0", style.iconClass)} />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className="text-sm font-medium text-foreground">{title}</p>
          <p className="text-xs text-muted-foreground">{body}</p>
          {detail && (
            <p className="text-xs text-muted-foreground/80">{detail}</p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {actionLabel && (
              <Button
                size="sm"
                disabled={authorizing}
                onClick={() => void onAuthorize()}
              >
                {authorizing ? "Redirecting…" : actionLabel}
              </Button>
            )}
            {variant === "connected" && (
              <Button size="sm" variant="outline" onClick={onDismiss}>
                Got it
              </Button>
            )}
          </div>
        </div>
        {dismissible && (
          <button
            type="button"
            aria-label="Dismiss"
            className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground"
            onClick={onDismiss}
          >
            <X className="size-4" />
          </button>
        )}
      </div>
    </div>
  );
}
